import { useEffect, useState } from "react"

import { useAppDispatch, useAppSelector } from "@src/hooks/use-redux"

import { getUser, logOut } from "@src/redux/user.slice"

import getGlobalSiteSettings from "@src/pages/api/get-global-site-settings"

import {
  GET_MY_USER_RECORD,
  REACTIVATE_RITUAL_PLUS,
  REMOVE_CARD_ON_FILE,
  REMOVE_FROM_RITUAL_PLUS,
  SKIP_RITUAL_PLUS_DELIVERY,
  UPDATE_CARD_ON_FILE,
  UPDATE_FREQUENCY_IN_RITUAL_PLUS,
  UPDATE_MY_BIRTHDATE,
  UPDATE_MY_PROFILE,
  UPDATE_QUANTITY_IN_RITUAL_PLUS,
} from "@src/operations/api/user"

import apolloClient from "@src/utilities/apollo-client"
import isDate from "@src/utilities/is-date"

import AccountLayout from "@src/layouts/account"
import AccountHeader from "@src/components/AccountHeader"
import AccountBirthday from "@src/components/AccountBirthday"
import AccountAddress from "@src/components/AccountAddress"
import AccountOrders from "@src/components/AccountOrders"
import AccountPaymentMethod from "@src/components/AccountPaymentMethod"
import AccountRitualPlus from "@src/components/AccountRitualPlus"
import LoadingFullScreen from "@src/components/LoadingFullScreen"
import YotpoLoyaltyLoader from "@src/components/yotpo-loyalty"

import type { MouseEventHandler } from "react"
import type { GetStaticPropsContext, InferGetStaticPropsType } from "next"
import type { DeepPartial } from "@src/entities"
import type {
  GetMyUserRecordQuery,
  Order,
  RemoveCardOnFileMutation,
  RemoveCardOnFileMutationVariables,
  ReactivateRitualPlusMutation,
  ReactivateRitualPlusMutationVariables,
  RemoveFromRitualPlusMutation,
  RemoveFromRitualPlusMutationVariables,
  SkipRitualPlusDeliveryMutation,
  SkipRitualPlusDeliveryMutationVariables,
  UpdateCardOnFileMutation,
  UpdateCardOnFileMutationVariables,
  UpdateMyBirthdateMutation,
  UpdateMyBirthdateMutationVariables,
  UpdateMyProfileMutation,
  UpdateMyProfileMutationVariables,
  UpdateRitualPlusFrequencyMutation,
  UpdateRitualPlusFrequencyMutationVariables,
  UpdateRitualPlusQuantityMutation,
  UpdateRitualPlusQuantityMutationVariables,
  User,
} from "@src/entities/types/api"
import type { AccountPaymentMethod as AccountPaymentMethodViewModel } from "@src/entities/view-models/account"

export default function AccountPage({
  siteSettings,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const dispatch = useAppDispatch()
  const user = useAppSelector(getUser)

  const [cardToken, setCardToken] = useState<string | null>(null)
  const [orders, setOrders] = useState<DeepPartial<Order>[]>([])
  const [paymentMethod, setPaymentMethod] = useState<
    AccountPaymentMethodViewModel | null | undefined
  >(null)
  const [paymentMethodErrorMessage, setPaymentMethodErrorMessage] = useState<
    string | null
  >(null)
  const [subscription, setSubscription] =
    useState<GetMyUserRecordQuery["myRitualPlus"]>()
  const [userRecord, setUserRecord] = useState<Partial<User>>({
    defaultAddress: {
      streetOne: "",
      streetTwo: "",
      city: "",
      state: "",
      zip: "",
      country: "US",
    },
    email: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
  })

  const [isLoadingPage, setIsLoadingPage] = useState<boolean>(true)
  const [isProcessingCreditCardChange, setIsProcessingCreditCardChange] =
    useState<boolean>(false)
  const [isUpdatingBirthday, setIsUpdatingBirthday] = useState<boolean>(false)

  const getMyUserRecord = async () => {
    const result = await apolloClient
      .query<GetMyUserRecordQuery>({
        query: GET_MY_USER_RECORD,
      })
      .catch((error) => {
        console.log(error)
      })
    if (result?.errors) {
      console.log(result?.errors)
    }
    setOrders(result?.data?.myOrderHistory.orders ?? [])
    setPaymentMethod(result?.data?.cardOnFile)
    setSubscription(result?.data?.myRitualPlus)
    setUserRecord(result?.data?.me ?? {})
    setIsLoadingPage(false)
  }

  const deletePaymentMethod: MouseEventHandler<HTMLAnchorElement> = (ev) => {
    ev.preventDefault()
    if (!isProcessingCreditCardChange) {
      setIsProcessingCreditCardChange(true)
      apolloClient
        .mutate<RemoveCardOnFileMutation, RemoveCardOnFileMutationVariables>({
          mutation: REMOVE_CARD_ON_FILE,
        })
        .then(() => getMyUserRecord())
        .then(() => {
          setIsProcessingCreditCardChange(false)
        })
    }
  }

  const onReactivateSubscription = async () =>
    apolloClient
      .mutate<
        ReactivateRitualPlusMutation,
        ReactivateRitualPlusMutationVariables
      >({
        mutation: REACTIVATE_RITUAL_PLUS,
      })
      .then(({ data }) => {
        setSubscription(data?.reactivateSubscription)
      })

  const onRemoveItem = async (
    input: RemoveFromRitualPlusMutationVariables["input"]
  ) =>
    apolloClient
      .mutate<
        RemoveFromRitualPlusMutation,
        RemoveFromRitualPlusMutationVariables
      >({
        mutation: REMOVE_FROM_RITUAL_PLUS,
        variables: { input },
      })
      .then(({ data }) => {
        setSubscription(data?.removeFromRitualPlus)
      })

  const onSkipNextDelivery = async (
    input: SkipRitualPlusDeliveryMutationVariables["input"]
  ) =>
    apolloClient
      .mutate<
        SkipRitualPlusDeliveryMutation,
        SkipRitualPlusDeliveryMutationVariables
      >({
        mutation: SKIP_RITUAL_PLUS_DELIVERY,
        variables: { input },
      })
      .then(({ data }) => {
        setSubscription(data?.skipRitualPlusDelivery)
      })

  const onUpdateBirthday = async (date: Date | null) => {
    setIsUpdatingBirthday(true)
    await apolloClient
      .mutate<UpdateMyBirthdateMutation, UpdateMyBirthdateMutationVariables>({
        mutation: UPDATE_MY_BIRTHDATE,
        variables: { date: isDate(date) ? date?.toISOString() : null },
      })
      .then(({ data }) => {
        setUserRecord((prevState) => ({
          ...prevState,
          birthDate: data?.setBirthDate,
        }))
        setIsUpdatingBirthday(false)
      })
  }

  const onUpdateFrequency = async (
    input: UpdateRitualPlusFrequencyMutationVariables["input"]
  ) =>
    apolloClient
      .mutate<
        UpdateRitualPlusFrequencyMutation,
        UpdateRitualPlusFrequencyMutationVariables
      >({
        mutation: UPDATE_FREQUENCY_IN_RITUAL_PLUS,
        variables: { input },
      })
      .then(({ data }) => {
        setSubscription(data?.updateRitualPlusFrequency)
      })

  const onUpdateQuantity = async (
    input: UpdateRitualPlusQuantityMutationVariables["input"]
  ) =>
    apolloClient
      .mutate<
        UpdateRitualPlusQuantityMutation,
        UpdateRitualPlusQuantityMutationVariables
      >({
        mutation: UPDATE_QUANTITY_IN_RITUAL_PLUS,
        variables: { input },
      })
      .then(({ data }) => {
        setSubscription(data?.updateRitualPlusQuantity)
      })

  const updatePaymentMethod = async (nonce: string) => {
    setPaymentMethodErrorMessage(null)
    setIsProcessingCreditCardChange(true)
    const { data, errors } = await apolloClient.mutate<
      UpdateCardOnFileMutation,
      UpdateCardOnFileMutationVariables
    >({
      mutation: UPDATE_CARD_ON_FILE,
      variables: {
        nonce,
      },
    })
    setIsProcessingCreditCardChange(false)
    if (errors) {
      setPaymentMethodErrorMessage(
        errors.map((error) => error.message).join(" ")
      )
    } else {
      setPaymentMethod(data?.updateCardOnFile)
    }
  }

  const updateProfile = async (
    input: UpdateMyProfileMutationVariables["input"]
  ): Promise<{ [key: string]: string } | null> => {
    const { data, errors } = await apolloClient.mutate<
      UpdateMyProfileMutation,
      UpdateMyProfileMutationVariables
    >({
      mutation: UPDATE_MY_PROFILE,
      variables: {
        input,
      },
    })
    if (errors && errors[0] && errors[0].extensions.code === "BAD_USER_INPUT") {
      return {
        [errors[0].extensions.argumentName]: errors[0].message,
      }
    }
    setUserRecord(data?.updateMe ?? {})
    return null
  }

  useEffect(() => {
    if (cardToken) {
      updatePaymentMethod(cardToken)
    }
  }, [cardToken])

  useEffect(() => {
    if (!user.authStateKnown) {
      return
    }
    if (user.isAnonymous) {
      window.location.href = "/account/login"
      return
    }
    getMyUserRecord()
  }, [user])

  return (
    <AccountLayout siteSettings={siteSettings}>
      <LoadingFullScreen showing={isLoadingPage} text="Loading your account" />
      {!isLoadingPage && (
        <div className="container-tablet font-browser">
          <AccountHeader
            firstName={userRecord.firstName ?? "there"}
            onSignOut={() => {
              dispatch(logOut()).then(() => {
                window.location.href = "/shop"
              })
            }}
          />
          <AccountAddress user={userRecord} onUpdateProfile={updateProfile} />
          <AccountBirthday
            birthDate={userRecord.birthDate}
            isUpdating={isUpdatingBirthday}
            onUpdateBirthDate={onUpdateBirthday}
          />
          {subscription && (
            <>
              <AccountRitualPlus
                onReactivateSubscription={onReactivateSubscription}
                onRemoveItem={onRemoveItem}
                onSkipNextDelivery={onSkipNextDelivery}
                onUpdateFrequency={onUpdateFrequency}
                onUpdateQuantity={onUpdateQuantity}
                subscription={subscription}
              />
              <AccountPaymentMethod
                canDeleteMethod={!subscription}
                errorMessage={paymentMethodErrorMessage}
                getCardToken={(nonce) => setCardToken(nonce)}
                paymentMethod={paymentMethod}
                isProcessing={isProcessingCreditCardChange}
                onDeletePaymentMethod={deletePaymentMethod}
              />
            </>
          )}
          <AccountOrders orders={orders} />
        </div>
      )}
      {userRecord.id && userRecord.email && (
        <YotpoLoyaltyLoader email={userRecord.email} uid={userRecord.id} />
      )}
    </AccountLayout>
  )
}

export const getStaticProps = async (context: GetStaticPropsContext) => {
  const siteSettings = await getGlobalSiteSettings(context.preview ?? false)

  return {
    props: {
      siteSettings,
    },
  }
}
